import React, { useState } from "react";
import { TextField } from "@/components/form/fields";
import Dropdown from "./Dropdown";
import { US_STATES } from "@/utils/constants/stateConstants";
import GooglePlacesAddressForm from "@/components/common/GooglePlacesAddressForm";

// Validation and formatting helpers
const validateZipCode = (zip: string, required: boolean = false) => {
  if (!zip) {
    return {
      valid: !required,
      message: required ? "ZIP code is required" : ""
    };
  }
  
  // ZIP code must be 5 digits or ZIP+4 format (12345 or 12345-6789)
  const zipRegex = /^\d{5}(-\d{4})?$/;
  const isValid = zipRegex.test(zip);
  
  return {
    valid: isValid,
    message: isValid ? "" : "Please enter a valid ZIP code (12345 or 12345-6789)"
  };
};

const formatZipCode = (value: string): string => {
  // Only allow digits
  const digits = value.replace(/\D/g, "");
  
  // Limit to 9 digits (ZIP or ZIP+4)
  const truncated = digits.substring(0, 9);
  
  // Format as ZIP or ZIP+4
  if (truncated.length <= 5) {
    return truncated;
  } else {
    return `${truncated.slice(0, 5)}-${truncated.slice(5)}`;
  }
};

export interface AddressFieldsProps {
  values: {
    streetAddress: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onStateChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  errors?: {
    streetAddress?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  className?: string;
  useGooglePlaces?: boolean; // New prop to enable Google Places integration
}

const AddressFieldSet: React.FC<AddressFieldsProps> = ({
  values,
  onChange,
  onStateChange,
  onBlur,
  errors = {},
  className = "",
  useGooglePlaces = true, // Enable by default
}) => {
  const [showAddressSearch, setShowAddressSearch] = useState(true);
  
  const handleStateChange = (value: string) => {
    if (onStateChange) {
      onStateChange(value);
    }
  };

  // Handle ZIP code change - let user type directly
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and dash
    const cleanValue = e.target.value.replace(/[^\d-]/g, '');
    
    if (cleanValue !== e.target.value) {
      // If we stripped characters, create a new event with cleaned value
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: cleanValue
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(newEvent);
    } else {
      // Otherwise pass the event through unchanged
      onChange(e);
    }
  };

  // Format ZIP code on blur
  const handleZipBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const formatted = formatZipCode(e.target.value);
    const zipValue = formatted.replace('-', '');
    
    // Custom validation for 5 or 9 digits
    let isValid = true;
    let errorMessage = '';
    
    if (zipValue.length > 0 && zipValue.length !== 5 && zipValue.length !== 9) {
      isValid = false;
      errorMessage = `ZIP code must have exactly 5 digits or 9 digits (ZIP+4). You entered ${zipValue.length} digits.`;
    } else if (zipValue.length > 0) {
      // Use the standard ZIP code validation
      const validationResult = validateZipCode(formatted);
      isValid = validationResult.valid;
      errorMessage = validationResult.message;
    }
    
    // Update the formatted value
    if (formatted !== e.target.value) {
      // Create a synthetic change event to update the field value
      const changeEvent = {
        target: {
          name: e.target.name,
          value: formatted
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      onChange(changeEvent);
    }
    
    // Report validation status to parent
    const errorEvent = {
      target: {
        name: e.target.name + '_error',
        value: errorMessage
      }
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    
    const validationEvent = {
      target: {
        name: e.target.name + '_valid',
        value: isValid ? 'true' : 'false'
      }
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    
    // Always report validation status
    onChange(validationEvent);
    
    // Only send error message if there is an error
    if (!isValid) {
      onChange(errorEvent);
    }
  };

  // Handle Google Places address selection
  const handleAddressChange = (address: any) => {
    // Create synthetic change events for each field
    const createChangeEvent = (name: string, value: string) => {
      return {
        target: {
          name,
          value
        }
      } as React.ChangeEvent<HTMLInputElement>;
    };

    // Update all fields in the form
    onChange(createChangeEvent('streetAddress', address.streetAddress));
    onChange(createChangeEvent('addressLine2', address.addressLine2 || ''));
    onChange(createChangeEvent('city', address.city));
    
    // For state, use the onStateChange handler if available
    if (onStateChange) {
      onStateChange(address.state);
    } else {
      onChange(createChangeEvent('state', address.state));
    }
    
    onChange(createChangeEvent('zipCode', address.zipCode));
  };

  // Option to toggle between Google Places and manual input
  const toggleAddressSearch = () => {
    setShowAddressSearch(!showAddressSearch);
  };

  return (
    <>
      {useGooglePlaces ? (
        <GooglePlacesAddressForm
          initialValues={{
            streetAddress: values.streetAddress,
            addressLine2: values.addressLine2 || '',
            city: values.city,
            state: values.state,
            zipCode: values.zipCode
          }}
          onAddressChange={handleAddressChange}
          errors={errors}
          onBlur={onBlur}
          className={className}
        />
      ) : (
        <>
          {/* Manual Entry Option */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mb-4">
            {/* Street Address Field */}
            <div className="md:col-span-2">
              <TextField
                id="streetAddress"
                name="streetAddress"
                label="Street Address"
                type="text"
                placeholder="123 Main St"
                value={values.streetAddress}
                onChange={onChange}
                onBlur={onBlur}
                error={errors.streetAddress}
                hint={errors.streetAddress}
              />
            </div>
            
            {/* Address Line 2 Field */}
            <div className="md:col-span-2">
              <TextField
                id="addressLine2"
                name="addressLine2"
                label="Suite/Unit Number"
                type="text"
                placeholder="Suite 101, Unit B, etc."
                value={values.addressLine2 || ""}
                onChange={onChange}
                onBlur={onBlur}
                error={errors.addressLine2}
                hint={errors.addressLine2}
              />
            </div>
            
            {/* City Field */}
            <div>
              <TextField
                id="city"
                name="city"
                label="City"
                type="text"
                placeholder="Enter city"
                value={values.city}
                onChange={onChange}
                onBlur={onBlur}
                error={errors.city}
                hint={errors.city}
              />
            </div>
            
            {/* State Field */}
            <div>
              <label id="state-label" htmlFor="state" className="block text-sm font-medium text-body-color dark:text-dark-6 mb-2">
                State
              </label>
              <Dropdown
                id="state"
                ariaLabelledBy="state-label"
                name="state"
                options={US_STATES}
                value={values.state}
                onChange={handleStateChange}
                error={errors.state}
              />
            </div>
            
            {/* ZIP Code Field */}
            <div>
              <TextField
                id="zipCode"
                name="zipCode"
                label="ZIP Code"
                type="text"
                placeholder="12345"
                value={values.zipCode}
                onChange={handleZipChange}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  handleZipBlur(e);
                  if (onBlur) onBlur(e);
                }}
                error={errors.zipCode}
                hint={errors.zipCode}
                maxLength={10}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AddressFieldSet; 